import { setSidebarCollapsed } from './sidebarResize';

export type SidebarTabId = 'file' | 'build' | 'support' | 'print' | 'simulate';

const STORAGE_KEY = 'rcsim-sidebar-tab';
const DEFAULT_TAB: SidebarTabId = 'build';

type TabDef = { id: SidebarTabId; label: string; icon: string };

const ICON_FILE = `<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z"/><path d="M14 3v5h5"/></svg>`;
const ICON_BUILD = `<svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="5" cy="17" r="2.25"/><circle cx="12" cy="7" r="2.25"/><circle cx="19" cy="17" r="2.25"/><path d="M7 16.2C8.8 12 10 8.8 12 8.8S15.2 12 17 16.2"/></svg>`;
const ICON_SUPPORT = `<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 20V9h3.5v11"/><path d="M10.25 20V4h3.5v16"/><path d="M16.5 20V11H20v9"/><path d="M3 20h18"/></svg>`;
const ICON_PRINT = `<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M7 8V3h10v5"/><path d="M7 17H5a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/><path d="M7 13h10v8H7z"/></svg>`;
const ICON_SIMULATE = `<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M8 5.5v13l11-6.5z"/></svg>`;

const TABS: TabDef[] = [
  { id: 'file', label: 'File', icon: ICON_FILE },
  { id: 'build', label: 'Build', icon: ICON_BUILD },
  { id: 'support', label: 'Support', icon: ICON_SUPPORT },
  { id: 'print', label: 'Print', icon: ICON_PRINT },
  { id: 'simulate', label: 'Simulate', icon: ICON_SIMULATE },
];

function isTabId(value: string): value is SidebarTabId {
  return TABS.some((t) => t.id === value);
}

function restoreTab(): SidebarTabId {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw && isTabId(raw)) return raw;
  } catch {
    // ignore
  }
  return DEFAULT_TAB;
}

function saveTab(id: SidebarTabId): void {
  try {
    localStorage.setItem(STORAGE_KEY, id);
  } catch {
    // ignore
  }
}

function applyTab(id: SidebarTabId): void {
  const tabs = document.getElementById('sidebar-tabs');
  const panes = document.getElementById('sidebar-panes');
  if (!tabs || !panes) return;
  tabs.querySelectorAll<HTMLButtonElement>('button[data-tab]').forEach((btn) => {
    const on = btn.dataset.tab === id;
    btn.classList.toggle('active', on);
    btn.setAttribute('aria-selected', on ? 'true' : 'false');
    btn.tabIndex = on ? 0 : -1;
  });
  panes.querySelectorAll<HTMLElement>('.sidebar-pane').forEach((pane) => {
    const on = pane.dataset.pane === id;
    pane.hidden = !on;
    pane.classList.toggle('is-active', on);
  });
  document.getElementById('sidebar')!.scrollTop = 0;
}

/** Icon tab bar + panes. Call before constructing sidebar panels. */
export function enableSidebarTabs(): void {
  const sidebar = document.getElementById('sidebar')!;

  const header = document.createElement('div');
  header.id = 'sidebar-header';

  const tablist = document.createElement('div');
  tablist.id = 'sidebar-tabs';
  tablist.setAttribute('role', 'tablist');
  tablist.setAttribute('aria-label', 'Side panel');

  for (const tab of TABS) {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.dataset.tab = tab.id;
    btn.title = tab.label;
    btn.setAttribute('aria-label', tab.label);
    btn.setAttribute('role', 'tab');
    btn.setAttribute('aria-controls', `sidebar-pane-${tab.id}`);
    btn.innerHTML = tab.icon;
    btn.addEventListener('click', () => setSidebarTab(tab.id, { expand: true }));
    tablist.appendChild(btn);
  }

  header.appendChild(tablist);

  const panes = document.createElement('div');
  panes.id = 'sidebar-panes';
  for (const tab of TABS) {
    const pane = document.createElement('div');
    pane.className = 'sidebar-pane';
    pane.id = `sidebar-pane-${tab.id}`;
    pane.dataset.pane = tab.id;
    pane.setAttribute('role', 'tabpanel');
    pane.setAttribute('aria-label', tab.label);
    pane.hidden = true;
    panes.appendChild(pane);
  }

  sidebar.prepend(header);
  sidebar.appendChild(panes);

  applyTab(restoreTab());
}

export function sidebarPane(id: SidebarTabId): HTMLElement {
  const el = document.querySelector<HTMLElement>(`#sidebar-panes > [data-pane="${id}"]`);
  if (!el) throw new Error(`Sidebar pane "${id}" is not mounted`);
  return el;
}

export function setSidebarTab(id: SidebarTabId, opts?: { expand?: boolean }): void {
  applyTab(id);
  saveTab(id);
  if (opts?.expand) setSidebarCollapsed(false);
}
